import { useLayoutEffect, useRef, useState, useTransition } from "react";

type HorizontalDrawerState = "open" | "closing" | "closed" | "placing" | "opening";

export interface HorizontalDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  show: boolean;
  width?: string;
  side?: "left" | "right";
  timeMs?: number;
  bodyLevel?: boolean;
  pushContent?: boolean;
  targetElement?: HTMLElement;
}

/**
 * A horizontally animated drawer that slides in/out from the side.
 * Uses a state machine pattern for consistent animation behavior.
 * Can work at the document body level to push content aside, or within a container.
 *
 * @param props - Destructured parameter object
 * @param props.children - The content to display inside the drawer
 * @param props.show - Whether the drawer should be visible (expanded)
 * @param props.width - The target width when visible (defaults to "600px")
 * @param props.side - The side from which the drawer slides ('left' or 'right', defaults to 'right')
 * @param props.timeMs - Animation duration in milliseconds for open/close transitions (defaults to 300)
 * @param props.bodyLevel - If true, drawer attaches to viewport with fixed positioning and can push content (defaults to false)
 * @param props.pushContent - If true when bodyLevel is enabled, pushes the target element content over instead of overlaying (defaults to true, only applies when bodyLevel is true)
 * @param props.targetElement - The element to push when drawer opens at body level (defaults to document.body, only applies when bodyLevel and pushContent are true)
 * @param props.style - Additional inline styles to apply to the drawer
 * @returns A drawer with horizontal slide animation
 */
export const HorizontalDrawer = ({
  show,
  width = "600px",
  side = "right",
  timeMs = 300,
  bodyLevel = false,
  pushContent = true,
  targetElement,
  children,
  style,
  ...divProps
}: HorizontalDrawerProps): React.ReactElement | null => {
  const [_isPending, startTransition] = useTransition();
  const [state, setState] = useState<HorizontalDrawerState>(show ? "open" : "closed");
  const [lastShow, setLastShow] = useState<boolean>(show);

  // Parse numeric width value for margin calculations
  const widthValue = parseInt(width, 10);
  const widthUnit = width.replace(/\d+/g, "");

  useLayoutEffect(() => {
    if (show !== lastShow) {
      if (show) {
        startTransition(() => setState("placing"));
      } else {
        startTransition(() => setState("closing"));
      }
      startTransition(() => setLastShow(show));
    }
  }, [state, show, lastShow]);

  // Store timeout IDs to clear them on cleanup
  const timeoutsRef = useRef<number[]>([]);
  const targetRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (state === "placing") {
      const id = window.setTimeout(() => {
        setState("opening");
      }, 5);
      timeoutsRef.current.push(id);
    }
    if (state === "opening") {
      const id = window.setTimeout(() => {
        setState("open");
      }, timeMs);
      timeoutsRef.current.push(id);
    }
    if (state === "closing") {
      const id = window.setTimeout(() => {
        setState("closed");
      }, timeMs);
      timeoutsRef.current.push(id);
    }

    // Cleanup: clear all scheduled timeouts
    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, [state, timeMs]);

  // Handle pushing content when drawer opens/closes (only when bodyLevel is enabled)
  useLayoutEffect(() => {
    if (!bodyLevel || !pushContent) {
      return;
    }

    // Store the target element in a ref to avoid ESLint warnings about modifying props
    targetRef.current = targetElement ?? document.body;
    const target = targetRef.current;
    const isOpen = ["open", "opening"].includes(state);
    const marginValue = isOpen ? `${widthValue}${widthUnit}` : "0";
    const marginProperty = side === "left" ? "marginLeft" : "marginRight";

    // Apply margin with transition
    target.style.transition = `margin ${timeMs}ms ease-in-out`;
    target.style[marginProperty] = marginValue;

    // Cleanup function to remove styles when component unmounts
    return () => {
      target.style[marginProperty] = "";
      target.style.transition = "";
    };
  }, [state, bodyLevel, pushContent, side, targetElement, widthValue, widthUnit, timeMs]);
  // For body-level drawers use transform-based sliding so height remains constant
  const bodyVisible = ["open", "opening"].includes(state);
  const translateX = bodyVisible ? "0%" : side === "right" ? "100%" : "-100%";

  return state === "closed" ? null : bodyLevel ? (
    <div
      {...divProps}
      style={{
        ...style,
        width: width,
        transform: `translateX(${translateX})`,
        transition: `transform ${timeMs}ms ease-in-out`,
        willChange: "transform",
        overflowX: "hidden",
        overflowY: "auto",
        position: "fixed",
        top: 0,
        [side]: 0,
        height: "100vh",
        zIndex: 1000,
      }}
    >
      {children}
    </div>
  ) : (
    <div
      style={{
        width: ["open", "opening"].includes(state) ? width : "0",
        transition: `width ${timeMs}ms ease-in-out`,
        overflowX: "hidden",
        overflowY: "auto",
        position: "relative",
        height: "100%",
        marginLeft: side === "right" ? "auto" : undefined,
        marginRight: side === "left" ? "auto" : undefined,
      }}
    >
      <div
        {...divProps}
        style={{
          ...style,
          width: width,
          position: "absolute",
          top: 0,
          [side === "right" ? "left" : "right"]: 0,
          height: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
};

HorizontalDrawer.displayName = "HorizontalDrawer";
